import React from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { useNavigate, Link } from 'react-router-dom';
import MobilePageLayout from '../../../components/layout/MobilePageLayout';
import { convertToVwMob } from '../../../Utils/responsive';
import DSLoading from '../../../components/designSystem/DSLoading';
import mainLogo from '../../../assets/Logo/main_logo.png';
import backgroundImage from '../../../assets/img/background.png';
import eyeOffIcon from '../../../assets/Icon/project/JoinMembershiIcons.png';
import { PATHS } from '../../../routes/paths';
import { usePasswordReset } from '../usePasswordReset';

const MAIN_TEXT = '#17171b';
const BTN_TEAL = '#026478';
const BTN_DISABLED_BG = '#e0e0e0';
const BTN_DISABLED_TEXT = '#9e9e9e';

type Props = { token: string };

const PasswordResetMob = ({ token }: Props) => {
  const { i18n, t } = useTranslation('translation');
  const navigate = useNavigate();
  const currentLang = (i18n.resolvedLanguage || i18n.language || 'ko').split('-')[0];
  const [showNewPassword, setShowNewPassword] = React.useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = React.useState(false);
  const [newPwFocused, setNewPwFocused] = React.useState(false);
  const [confirmPwFocused, setConfirmPwFocused] = React.useState(false);
  const {
    phase,
    record,
    password,
    setPassword,
    confirmPassword,
    setConfirmPassword,
    passwordMismatchError,
    isSubmitting,
    isSubmitDisabled,
    handleSubmit,
  } = usePasswordReset(token);

  const goLogin = () => {
    navigate(PATHS.LOGIN);
  };

  const goToMain = () => {
    navigate(PATHS.DASHBOARD);
  };

  const renderBody = () => {
    if (phase === 'loading') {
      return (
        <LoadingWrap>
          <DSLoading variant="content" size={200} marginTop={convertToVwMob(24)} />
        </LoadingWrap>
      );
    }
    if (phase === 'invalid') {
      return (
        <ContentColumn>
          <Title>{t('page.passwordReset.invalidTitle')}</Title>
          <InvalidBody>
            {t('page.passwordReset.invalidDescriptionLine1')}
            <br />
            {t('page.passwordReset.invalidDescriptionLine2')}
          </InvalidBody>
          <PrimaryButton type="button" onClick={goLogin}>
            {t('page.passwordReset.goLogin')}
          </PrimaryButton>
        </ContentColumn>
      );
    }
    if (phase === 'success') {
      return null;
    }
    const hasMismatch = Boolean(passwordMismatchError);
    const ph = t('page.passwordReset.passwordInputPlaceholder');
    const showFloatNew = newPwFocused || Boolean(password);
    const showFloatConfirm = confirmPwFocused || Boolean(confirmPassword);
    return (
      <ContentColumn>
        <Title>{t('page.passwordReset.title')}</Title>
        <InstructionText>{t('page.passwordReset.descriptionLine1')}</InstructionText>
        {record?.user_email ? <EmailText>{record.user_email}</EmailText> : null}
        <Form onSubmit={handleSubmit}>
          <MaLabeledFieldBlock>
            <MaFieldCaption htmlFor="pw-reset-mob-new">{t('page.passwordReset.passwordLabel')}</MaFieldCaption>
            <MaInputBox $error={hasMismatch} $active={showFloatNew}>
              {showFloatNew ? <MaInnerLabel htmlFor="pw-reset-mob-new">{ph}</MaInnerLabel> : null}
              <MaInputRow>
                <MaFieldInput
                  id="pw-reset-mob-new"
                  type={showNewPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  onFocus={() => setNewPwFocused(true)}
                  onBlur={() => setNewPwFocused(false)}
                  placeholder={showFloatNew ? '' : ph}
                  autoComplete="new-password"
                />
                {password ? (
                  <MaInputActions>
                    <IconButton type="button" onClick={() => setShowNewPassword((v) => !v)} aria-label="toggle password">
                      {showNewPassword ? <EyeOnSvg /> : <EyeOffImg />}
                    </IconButton>
                    <IconButton
                      type="button"
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => setPassword('')}
                      aria-label="clear password"
                    >
                      <ClearSvg />
                    </IconButton>
                  </MaInputActions>
                ) : null}
              </MaInputRow>
            </MaInputBox>
          </MaLabeledFieldBlock>
          <MaLabeledFieldBlock>
            <MaFieldCaption htmlFor="pw-reset-mob-confirm">{t('page.passwordReset.passwordConfirmLabel')}</MaFieldCaption>
            <MaInputErrorStack>
              <MaInputBox $error={hasMismatch} $active={showFloatConfirm}>
                {showFloatConfirm ? <MaInnerLabel htmlFor="pw-reset-mob-confirm">{ph}</MaInnerLabel> : null}
                <MaInputRow>
                  <MaFieldInput
                    id="pw-reset-mob-confirm"
                    type={showConfirmPassword ? 'text' : 'password'}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    onFocus={() => setConfirmPwFocused(true)}
                    onBlur={() => setConfirmPwFocused(false)}
                    placeholder={showFloatConfirm ? '' : ph}
                    autoComplete="new-password"
                  />
                  {confirmPassword ? (
                    <MaInputActions>
                      <IconButton type="button" onClick={() => setShowConfirmPassword((v) => !v)} aria-label="toggle password">
                        {showConfirmPassword ? <EyeOnSvg /> : <EyeOffImg />}
                      </IconButton>
                      <IconButton
                        type="button"
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={() => setConfirmPassword('')}
                        aria-label="clear confirm password"
                      >
                        <ClearSvg />
                      </IconButton>
                    </MaInputActions>
                  ) : null}
                </MaInputRow>
              </MaInputBox>
              {passwordMismatchError ? <MaFieldErrorText>{passwordMismatchError}</MaFieldErrorText> : null}
            </MaInputErrorStack>
          </MaLabeledFieldBlock>
          <SubmitButton type="submit" disabled={isSubmitDisabled}>
            {isSubmitting ? t('page.passwordReset.actions.submitting') : t('page.passwordReset.actions.submit')}
          </SubmitButton>
        </Form>
      </ContentColumn>
    );
  };

  return (
    <MobilePageLayout title="" hideHeader hideFooter showBottomNav={false}>
      <BackgroundOverlay />
      <HeaderRow>
        <LangSelect value={currentLang} onChange={(e) => i18n.changeLanguage(e.target.value)}>
          <option value="ko">{t('lang.ko')}</option>
          <option value="en">{t('lang.en')}</option>
        </LangSelect>
      </HeaderRow>
      <Container>
        <LogoSection>
          <LogoLink to={PATHS.LOGIN}>
            <Logo src={mainLogo} alt="Mission Fund" />
          </LogoLink>
        </LogoSection>
        {renderBody()}
      </Container>
      {phase === 'success' ? (
        <SuccessModalOverlay>
          <SuccessModalCard>
            <SuccessModalTitle>{t('page.passwordReset.successTitle')}</SuccessModalTitle>
            <SuccessModalDescription>
              {t('page.passwordReset.successBodyLine1')}
              <br />
              {t('page.passwordReset.successBodyLine2Mob')}
              <br />
              {t('page.passwordReset.successBodyLine3Mob')}
            </SuccessModalDescription>
            <SuccessModalButton type="button" onClick={goToMain}>
              {t('page.passwordReset.successButtonMob')}
            </SuccessModalButton>
          </SuccessModalCard>
        </SuccessModalOverlay>
      ) : null}
    </MobilePageLayout>
  );
};

const EyeOnSvg = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M12 5C7 5 2.73 8.11 1 12.5C2.73 16.89 7 20 12 20C17 20 21.27 16.89 23 12.5C21.27 8.11 17 5 12 5ZM12 17.5C9.24 17.5 7 15.26 7 12.5C7 9.74 9.24 7.5 12 7.5C14.76 7.5 17 9.74 17 12.5C17 15.26 14.76 17.5 12 17.5ZM12 9.5C10.34 9.5 9 10.84 9 12.5C9 14.16 10.34 15.5 12 15.5C13.66 15.5 15 14.16 15 12.5C15 10.84 13.66 9.5 12 9.5Z"
      fill="#929292"
    />
  </svg>
);

const EyeOffImg = () => <img src={eyeOffIcon} alt="" width="24" height="24" />;

const ClearSvg = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="12" cy="12" r="10" fill="#BCBCBC" />
    <path d="M8 8L16 16M16 8L8 16" stroke="white" strokeWidth="2" strokeLinecap="round" />
  </svg>
);

const BackgroundOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-image: url(${backgroundImage});
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  z-index: 0;
  pointer-events: none;
`;

/** 로그인 페이지(`Login/mob`)와 동일 */
const Container = styled.div`
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  padding-bottom: ${convertToVwMob(40)};
  box-sizing: border-box;
`;

const HeaderRow = styled.div`
  position: relative;
  z-index: 1;
  width: 100%;
  display: flex;
  justify-content: flex-end;
`;

const LangSelect = styled.select`
  padding: ${convertToVwMob(12)} ${convertToVwMob(36)} ${convertToVwMob(12)} ${convertToVwMob(16)};
  border-radius: ${convertToVwMob(8)};
  border: 1px solid ${({ theme }) => theme.colors.grayScale[20]};
  background: white;
  color: ${({ theme }) => theme.colors.grayScale[70]};
  font-size: ${convertToVwMob(18)};
  cursor: pointer;
  outline: none;
  min-height: ${convertToVwMob(44)};
  appearance: none;
  -webkit-appearance: none;
  -moz-appearance: none;
  background-image: url("data:image/svg+xml,%3Csvg width='14' height='8' viewBox='0 0 14 8' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M1 1L7 7L13 1' stroke='%23929292' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right ${convertToVwMob(12)} center;
  background-size: ${convertToVwMob(14)} ${convertToVwMob(8)};
`;

const LogoSection = styled.div`
  margin-top: ${convertToVwMob(40)};
  /* 로고 ↔ 제목(비밀번호 변경) 54px */
  margin-bottom: ${convertToVwMob(54)};
`;

const LogoLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  text-decoration: none;
`;

const Logo = styled.img`
  width: ${convertToVwMob(239)};
  height: ${convertToVwMob(59)};
  object-fit: contain;
`;

const ContentColumn = styled.div`
  width: 100%;
  max-width: 400px;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
`;

const Title = styled.h1`
  margin: 0 0 ${convertToVwMob(16)};
  /* 제목 ↔ 안내 문구 16px */
  color: ${MAIN_TEXT};
  text-align: center;
  font-family: Pretendard, sans-serif;
  font-size: ${convertToVwMob(32)};
  font-style: normal;
  font-weight: 700;
  line-height: ${convertToVwMob(28)};
`;

const InstructionText = styled.p`
  margin: 0;
  color: ${MAIN_TEXT};
  text-align: center;
  font-family: Pretendard, sans-serif;
  font-size: ${convertToVwMob(20)};
  font-style: normal;
  font-weight: 500;
  line-height: 150%;
  padding: 0 ${convertToVwMob(8)};
`;

const InvalidBody = styled.p`
  margin: ${convertToVwMob(12)} 0 0;
  color: ${MAIN_TEXT};
  text-align: center;
  font-family: Pretendard, sans-serif;
  font-size: ${convertToVwMob(16)};
  font-weight: 400;
  line-height: 150%;
  padding: 0 ${convertToVwMob(8)};
`;

const EmailText = styled.p`
  margin: ${convertToVwMob(36)} 0 0;
  /* 안내 문구 ↔ 이메일 36px */
  color: ${MAIN_TEXT};
  text-align: center;
  font-family: Pretendard, sans-serif;
  font-size: ${convertToVwMob(24)};
  font-style: normal;
  font-weight: 500;
  line-height: 150%;
  word-break: break-all;
  padding: 0 ${convertToVwMob(4)};
`;

const Form = styled.form`
  width: 100%;
  /* 이메일 ↔ 첫 입력 필드 블록 20px (이메일 없을 때는 안내↔폼 20px) */
  margin-top: ${convertToVwMob(20)};
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${convertToVwMob(20)};
`;

/** 캡션–입력 8px (나의 계정 FieldGroup 과 동일) */
const MaLabeledFieldBlock = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
`;

const MaFieldCaption = styled.label`
  margin-bottom: ${convertToVwMob(8)};
  color: ${MAIN_TEXT};
  font-family: Pretendard, sans-serif;
  font-size: ${convertToVwMob(16)};
  font-weight: 700;
  line-height: 140%;
`;

/** 나의 계정 InputGroup: 박스–에러 gap 4px */
const MaInputErrorStack = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  gap: ${convertToVwMob(4)};
`;

/** 나의 계정 InputBox 와 동일 */
const MaInputBox = styled.div<{ $error?: boolean; $active?: boolean }>`
  box-sizing: border-box;
  width: 100%;
  min-height: ${({ $active }) => ($active ? convertToVwMob(74) : convertToVwMob(54))};
  background-color: #ffffff;
  border: ${({ $error, $active, theme }) =>
    $error ? '1.6px solid #ff0000' : $active ? `1.6px solid ${theme.colors.primaryBlue[40]}` : `1px solid ${theme.colors.grayScale[20]}`};
  border-radius: ${convertToVwMob(4)};
  padding: ${convertToVwMob(8)} ${convertToVwMob(12)};
  display: flex;
  flex-direction: column;
  gap: ${({ $active }) => ($active ? convertToVwMob(2) : '0')};
  position: relative;
  justify-content: ${({ $active }) => ($active ? 'flex-start' : 'center')};
  text-align: left;
`;

const MaInnerLabel = styled.label`
  ${({ theme }) => theme.typography.contents.regular16};
  color: ${({ theme }) => theme.colors.grayScale[60]};
  text-align: left;
  width: 100%;
`;

/** 입력 + 아이콘: flex, align-items center, gap 16px */
const MaInputRow = styled.div`
  display: flex;
  width: 100%;
  align-items: center;
  gap: ${convertToVwMob(16)};
`;

const MaFieldInput = styled.input`
  flex: 1;
  min-width: 0;
  border: none;
  background: transparent;
  outline: none;
  padding: 0;
  text-align: left;
  ${({ theme }) => theme.typography.contents.regular24};
  color: ${({ theme }) => theme.colors.grayScale[90]};

  &::placeholder {
    color: ${({ theme }) => theme.colors.grayScale[50]};
  }
`;

const MaInputActions = styled.div`
  display: flex;
  align-items: center;
  gap: ${convertToVwMob(8)};
  flex-shrink: 0;
`;

/** 나의 계정 ErrorText 와 동일 (박스–문구 간격은 MaInputErrorStack gap) */
const MaFieldErrorText = styled.p`
  ${({ theme }) => theme.typography.contents.regular20};
  color: #ff0000;
  margin: 0;
  text-align: left;
  width: 100%;
`;

const IconButton = styled.button`
  width: ${convertToVwMob(24)};
  height: ${convertToVwMob(24)};
  border: none;
  background: transparent;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  flex-shrink: 0;
`;

const SubmitButton = styled.button`
  width: 100%;
  height: ${convertToVwMob(52)};
  margin-top: ${convertToVwMob(8)};
  border: none;
  border-radius: ${convertToVwMob(12)};
  background: ${({ disabled }) => (disabled ? BTN_DISABLED_BG : 'var(--PrimaryBlue-80, #026478)')};
  color: ${({ disabled }) => (disabled ? BTN_DISABLED_TEXT : '#ffffff')};
  font-family: Pretendard, sans-serif;
  font-size: ${convertToVwMob(17)};
  font-weight: 600;
  line-height: 150%;
  cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
`;

const PrimaryButton = styled.button`
  width: 100%;
  height: ${convertToVwMob(52)};
  margin-top: ${convertToVwMob(28)};
  border: none;
  border-radius: ${convertToVwMob(8)};
  background: ${BTN_TEAL};
  color: #ffffff;
  font-family: Pretendard, sans-serif;
  font-size: ${convertToVwMob(17)};
  font-weight: 600;
  cursor: pointer;
`;

const LoadingWrap = styled.div`
  width: 100%;
  min-height: ${convertToVwMob(200)};
  display: flex;
  align-items: center;
  justify-content: center;
`;

const SuccessModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(23, 23, 27, 0.42);
  backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: ${convertToVwMob(20)};
`;

const SuccessModalCard = styled.div`
  display: flex;
  width: min(${convertToVwMob(320)}, calc(100vw - ${convertToVwMob(40)}));
  padding: ${convertToVwMob(36)} ${convertToVwMob(24)} ${convertToVwMob(28)};
  flex-direction: column;
  align-items: center;
  gap: ${convertToVwMob(20)};
  border-radius: ${convertToVwMob(22)};
  background: #fff;
  box-shadow: 0 0 ${convertToVwMob(12)} 0 rgba(0, 0, 0, 0.35);
`;

const SuccessModalTitle = styled.h2`
  margin: 0;
  color: ${MAIN_TEXT};
  text-align: center;
  font-family: Pretendard, sans-serif;
  font-size: ${convertToVwMob(22)};
  font-weight: 700;
  line-height: 130%;
`;

const SuccessModalDescription = styled.p`
  margin: 0;
  color: ${MAIN_TEXT};
  text-align: center;
  font-family: Pretendard, sans-serif;
  font-size: ${convertToVwMob(18)};
  font-weight: 500;
  line-height: 150%;
`;

const SuccessModalButton = styled.button`
  width: 100%;
  height: ${convertToVwMob(52)};
  margin-top: ${convertToVwMob(4)};
  padding: 0 ${convertToVwMob(10)};
  display: flex;
  justify-content: center;
  align-items: center;
  border: none;
  border-radius: ${convertToVwMob(12)};
  background: ${BTN_TEAL};
  color: #fff;
  text-align: center;
  font-family: Pretendard, sans-serif;
  font-size: ${convertToVwMob(18)};
  font-weight: 600;
  line-height: 150%;
  cursor: pointer;
`;

export default PasswordResetMob;
